import React, { useState } from 'react';
import {
  Box,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  Paper,
  IconButton,
  useTheme,
} from '@mui/material';
import { LockOutlined, Email, Visibility, VisibilityOff, Brightness4, Brightness7 } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { sileo } from 'sileo';
import { useNavigate } from 'react-router-dom';
import { LoadingOrb } from '../components/LoadingOrb';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login } = useAuth();
  const { mode, toggleMode } = useThemeMode();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Por favor completa todos los campos');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      
      await login(email, password);
      navigate('/dashboard');
      
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error en el login';
      setError(errorMessage);
      sileo.error({
        title: 'No pudimos iniciar sesión',
        description: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDark = mode === 'dark';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: isDark
          ? 'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)'
          : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2,
        position: 'relative',
      }}
    >
      <IconButton
        onClick={toggleMode}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          color: 'white',
          bgcolor: 'rgba(255,255,255,0.2)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
        }}
        title={mode === 'dark' ? 'Modo claro' : 'Modo oscuro'}
      >
        {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
      </IconButton>
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            background: (theme) => theme.palette.mode === 'dark' 
              ? 'rgba(30, 30, 30, 0.95)' 
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          {/* Header del login */}
          <Box
            sx={{
              background: isDark
                ? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
                : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
              padding: 4,
              textAlign: 'center',
              color: 'white',
            }}
          >
            <LockOutlined sx={{ fontSize: 48, marginBottom: 2 }} />
            <Typography variant="h4" component="h1" gutterBottom>
              Sinaptya
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Sistema de Evaluaciones Neuropsicológicas
            </Typography>
          </Box>

          {/* Formulario de login */}
          <CardContent sx={{ padding: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ mb: 4 }}>
              Iniciar Sesión
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                InputProps={{
                  startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />,
                }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Contraseña"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                margin="normal"
                required
                InputProps={{
                  startAdornment: <LockOutlined sx={{ mr: 1, color: 'text.secondary' }} />,
                  endAdornment: (
                    <Button
                      onClick={() => setShowPassword(!showPassword)}
                      sx={{ minWidth: 'auto', p: 1 }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </Button>
                  ),
                }}
                sx={{ mb: 3 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={isSubmitting}
                sx={{
                  py: 1.5,
                  fontSize: '1.1rem',
                  background: isDark
                    ? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)'
                    : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
                  '&:hover': {
                    background: isDark
                      ? 'linear-gradient(135deg, #0d47a1 0%, #0277bd 100%)'
                      : 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
                  },
                }}
              >
                {isSubmitting ? (
                  <LoadingOrb size={20} />
                ) : (
                  'Iniciar Sesión'
                )}
              </Button>
            </Box>

            {/* Información adicional */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Acceso solo para profesionales autorizados
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Contacta al administrador para obtener credenciales
              </Typography>
            </Box>
          </CardContent>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;